import {
  forwardRef,
  useState,
  useMemo,
  useEffect,
  useCallback,
  useImperativeHandle,
  useRef,
} from "react";
import Editor from "froala-editor";
import FroalaEditor from "react-froala-wysiwyg";
import { Color, Icon } from "@utdanningsdirektoratet/icon";
import { Tooltip } from "@utdanningsdirektoratet/tooltip";
import { useId } from "@utdanningsdirektoratet/utils/useId";
import { cls } from "@utdanningsdirektoratet/utils/cls";
import { assert } from "@utdanningsdirektoratet/utils/assert";
import DOMPurify from "dompurify";
import baseConfig from "./baseConfig";
import baseDomPurifyConfig from "./baseDomPurifyConfig";
import { RichTextRef, RichTextProps, LanceApp, Lance, Config } from "./types";
import styles from "./RichText.module.less";

import "froala-editor/js/plugins.pkgd.min.js";
import "froala-editor/js/plugins/track_changes.min.js";
import "froala-editor/js/languages/nb.js";
import Comments from "./Comments";

require("./plugins/lance/plugin.js");
require("./plugins/lance/js/annotationsui.js");

declare global {
  interface Window {
    froalaLicense: string;
    LANCE: {
      AnnotationsUI: { new (): LanceApp };
      initFroalaLancePlugin: (FroalaEditor: typeof Editor) => void;
    };
  }
}

const sanitizeHTML = (value: string, config: Partial<Config>): string => {
  // DOMPurify doesn't allow <annotation> tags for some reason. LANCE uses <annotation> so we have to do a hack to make them work together
  // https://github.com/cure53/DOMPurify/issues/719
  const tempValue = (value ?? "")
    .replace(/<annotation/gi, "<annotations")
    .replace(/<\/annotation/gi, "</annotations");
  const sanitized = DOMPurify.sanitize(
    DOMPurify.sanitize(tempValue, config) as string,
    config,
  ) as string;
  return sanitized
    .replace(/<annotations/gi, "<annotation")
    .replace(/<\/annotations/gi, "</annotation");
};

const RichText = forwardRef<RichTextRef, RichTextProps>((props, ref) => {
  const id = useId(props.id);
  const [editor, setEditor] = useState<Editor>();
  const [lance, setLance] = useState<Lance>();
  const [lanceInitialized, setLanceInitialized] = useState(false);
  const [lanceGlobals] = useState(window.LANCE);
  const [isFocused, setFocused] = useState(false);
  const internalState = useRef(props.value ?? "");

  const {
    lazy,
    autoFocus,
    onBlur,
    onFocus,
    onChange,
    onInit,
    info,
    disabled,
    document,
    message,
    label,
    sidebarExpanded = "hascomments",
    error,
    erroralways,
    icon = info && !error ? "circle info" : "warning",
    className,
    customEvents = {},
    options,
    value,
    license,
  } = props;

  const [isSidebarOpen, setSidebarOpen] = useState(
    (sidebarExpanded === "hascomments" &&
      (props.value ?? "").indexOf("<annotation") !== -1) ||
      sidebarExpanded === "always",
  );
  const config = useMemo(
    () => ({ ...baseConfig, ...baseDomPurifyConfig, ...props.config }),
    [props.config],
  );

  useEffect(() => {
    if (onInit) onInit(Editor as any);
    config.onInit.forEach((cb) => {
      cb(Editor as any, props);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (editor && editor.edit) {
      if (disabled) {
        editor.edit.off();
      } else {
        editor.edit.on();
      }
    }
  }, [editor, disabled]);

  useEffect(() => {
    if (autoFocus && editor && editor.events) editor.events.focus();
  }, [editor, autoFocus]);

  useEffect(() => {
    assert(!!license, 'Mangler Froala lisens, "license" er undefined');
  }, [license]);

  useEffect(() => {
    if (editor && internalState.current !== value) {
      editor.html.set(sanitizeHTML(value, config));
    }
  }, [config, editor, value]);

  useEffect(() => {
    if (!lanceGlobals) {
      return;
    }
    if (lanceInitialized) {
      return;
    }
    lanceGlobals.initFroalaLancePlugin(Editor);
    setLanceInitialized(true);
  }, [lanceGlobals, lanceInitialized]);

  const { users, currentUserId } = config.lance ?? {};

  useEffect(() => {
    if (lance && users)
      lance.getAnnotations().addUsers(
        Object.entries(users).map(([userId, userName]) => ({
          id: userId,
          name: userName,
        })),
      );
  }, [lance, users]);

  useEffect(() => {
    if (lance && currentUserId) lance.getAnnotations().setUserId(currentUserId);
  }, [lance, currentUserId]);

  /* eslint-disable react/no-this-in-sfc */
  const onFocusWrapped = useCallback(
    function onFocusWrapped(this: Editor): void {
      if (onFocus) onFocus(this.html ? this.html.get() : undefined);
      setFocused(true);
    },
    [onFocus],
  );

  const onBlurWrapped = useCallback(
    function onBlurWrapped(this: Editor): void {
      if (onBlur) onBlur(this.html ? this.html.get() : undefined);
      setFocused(false);
    },
    [onBlur],
  );

  const onPaste = useCallback(
    (_html: string): string => {
      if (_html && _html.replace) {
        // Edge legger til \n innimellom tekst i stedet for spaces av en eller annen grunn
        // eslint-disable-next-line prefer-regex-literals
        return sanitizeHTML(_html.replace(new RegExp("\\n", "g"), " "), config);
      }
      return sanitizeHTML(_html, config);
    },
    [config],
  );

  const onHtmlGet = useCallback(
    (_html: string): string => {
      return sanitizeHTML(_html.replace(/id="isPasted"/g, ""), config);
    },
    [config],
  );

  const onInitialized = useCallback(async function onInitialized(
    this: Editor,
  ): Promise<void> {
    // Hack for å få editoren på plass før state-change re-render
    await new Promise((resolve) => {
      setTimeout(resolve, 1);
    });
    if (this.edit) {
      if (disabled) {
        this.edit.off();
      } else {
        this.edit.on();
      }
    }
    if (autoFocus && this.events) this.events.focus();
    // this.html.set(sanitizeHTML(value, config));
    // onChange(this.html.get());
    setEditor(this);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /* eslint-enable react/no-this-in-sfc */

  const onLanceInitialized = useCallback(
    function onLanceInitialized(this: Editor, arg: { lance: Lance }): void {
      const _lance = arg && arg.lance;
      setLance(_lance);
      config.lance?.onInit(id, _lance, this);
    },
    [id, config.lance],
  );

  const insertAtCursor: RichTextRef["insertAtCursor"] = (
    _type,
    _value,
    _id = _value,
  ) => {
    if (!disabled && editor && editor.html && editor.selection && editor.undo) {
      if (_type === "text") {
        editor.html.insert(_value, false);
      } else {
        const classname = `RichText-placeholder RichText-placeholder-${_type}`;

        editor.html.insert(
          `<input type="button" class="${classname}" disabled="disabled" value="${_value}" data-id="${_id}" />`,
          false,
        );
      }

      editor.undo.saveStep(undefined);

      editor.selection.save();
      // onChange(editor.html.get());
      editor.selection.restore();
    }
  };

  const focus: RichTextRef["focus"] = () => {
    if (editor && editor.events) editor.events.focus();
  };

  useImperativeHandle(ref, () => ({
    focus,
    insertAtCursor,
  }));

  const richTextClass = cls({
    [styles.richText]: true,
    [styles.richTextError]: error,
    [styles.richTextInfo]: info,
    [styles.richTextLazy]: lazy,
    [styles.richTextDisabled]: disabled,
    [styles.richTextDocument]: document,
    [styles.richTextFocused]: isFocused,
  });

  const iconClass = cls({
    [styles.richTextIcon]: true,
    [styles.richTextIconVisible]: error || info,
  });

  const labelClass = cls({
    [styles.richTextLabel]: true,
    [styles.richTextLabelPadding]: (error || info) && icon && !label,
    [styles.richTextLabelFocused]: isFocused,
  });

  const configuration = useMemo(
    () => ({
      ...options,
      ...config,
      key: license,
      initOnClick: lazy,
      attribution: false,
      htmlUntouched: true,
      events: {
        focus: onFocusWrapped,
        blur: onBlurWrapped,
        "paste.afterCleanup": onPaste,
        "html.get": onHtmlGet,
        initialized: onInitialized,
        "lance::init": onLanceInitialized,
        "lance::open": () => {
          setSidebarOpen(true);
        },
        ...(config.customEvents ?? {}),
        ...customEvents,
      },
    }),
    [
      config,
      customEvents,
      lazy,
      license,
      onBlurWrapped,
      onFocusWrapped,
      onHtmlGet,
      onInitialized,
      onLanceInitialized,
      onPaste,
      options,
    ],
  );

  let iconColor;
  if (info) iconColor = "darkblue" as Color;
  if (error) iconColor = "darkred" as Color;

  return (
    <Tooltip
      className={className}
      message={message}
      disabled={!(error || info)}
      alwaysShow={erroralways === undefined ? error : erroralways}
      align="right"
      id={`${id}-tooltip`}
      alert
    >
      <label htmlFor={id} id={`${id}-label`} className={labelClass}>
        {label}
        {icon ? (
          <Icon
            className={iconClass}
            color={iconColor}
            aria-live="polite"
            aria-hidden={error || info ? "false" : "true"}
            icon={icon}
          />
        ) : null}
      </label>

      <div className={styles.richTextComments}>
        <div
          id={id}
          aria-multiline="true"
          aria-invalid={error}
          aria-label={props["aria-label"]}
          aria-labelledby={label ? `${id}-label` : undefined}
          className={richTextClass}
        >
          <FroalaEditor
            tag="textarea"
            config={configuration}
            model={value}
            onModelChange={(html: string) => {
              internalState.current = html;
              onChange(html);
            }}
          />
        </div>
        {config.lance && (
          <Comments id={id} isOpen={isSidebarOpen} setOpen={setSidebarOpen} />
        )}
      </div>
    </Tooltip>
  );
});

/**
 * Check if user is commenting by searching for any open comment edit/input field
 *
 * @param richTextId Search for open comments within the RichText-element with this given ID.
 * @returns True if any open comments are found
 */
export const isCommenting = (richTextId: string): boolean => {
  const annotationsWrapperId = `annotations-wrapper-${richTextId.replaceAll(":", "\\:")}`;
  const isInsertingComment = document.querySelector(
    `#${annotationsWrapperId} [data-comment-insert="true"]`,
  );
  const isEditingComment = document.querySelector(
    `#${annotationsWrapperId} [data-comment-edit="true"]`,
  );
  return isInsertingComment !== null || isEditingComment !== null;
};

export default RichText;
